import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  Home,
  Building,
  Package,
  Users,
  Bell,
  Settings,
  Menu,
  X,
  User,
  Receipt,
  Calculator,
  DollarSign,
  ClipboardList,
  UserPlus,
  FilePlus,
  Store,
  FileText as FileTextIcon,
  Shield,
  FolderOpen,
  FolderKanban,
  ShoppingBag,
  LockKeyhole,
  FolderPlus,
  Search,
  ChevronRight,
  ArrowLeft,
  CircleHelp
} from "lucide-react";
import styles from "./BotoneraModern.module.scss";
import BotonModern from "./BotonModern.tsx";
import LogOutMenu from "../Perfil/LogOutMenu/LogOutMenu";
import useUserImage from "../../hooks/useUserImage";
import logoNavbar from "../../assets/images/Logo-edify-no-bg.png";
import { buildProfileUrl, getProfileTabFromQuery, PROFILE_SUBITEM_TAB_MAPPING, PROFILE_TAB_TO_NAV_ITEM, getProfilePathInfo, getUserDisplayName } from "../../utils/navigationHelpers.ts";
import { openFeedTextModal } from "../../store/actions";
import ModalOverlay from "../UI/ModalOverlay/ModalOverlay.tsx";

interface BotoneraModernProps {
  mobileStylesFromParent?: boolean;
  userData: any;
  newNotifications: number;
  profileVisualizerData?: any;
  openFeedTextModal?: (config: { text: boolean }) => void;
}

interface NavSubItem {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  directRoute?: string;
}

interface NavButton {
  id: string;
  nombre: string;
  icono: React.ReactNode;
  color: string;
  route: string;
  badge?: number;
  adminOnly?: boolean;
  hasSubmenu?: boolean;
  subItems?: NavSubItem[];
}

const BotoneraModern: React.FC<BotoneraModernProps> = ({
  mobileStylesFromParent,
  userData,
  newNotifications,
  profileVisualizerData,
  openFeedTextModal
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeRoute, setActiveRoute] = useState<string>("");
  const [menuScreen, setMenuScreen] = useState<"main" | string>("main");
  const navigate = useNavigate();
  const location = useLocation();
  const profilePathInfo = useMemo(() => getProfilePathInfo(location.pathname), [location.pathname]);

  const { imageError, initials, profileImage } = useUserImage(userData);
  const isUserAdmin = Boolean(userData?.rols?.some((rol: any) => rol.name === "ADMIN"));

  const navButtons: NavButton[] = [
    {
      id: "dashboard",
      nombre: "Inicio",
      icono: <Home size={20} />,
      color: "#234ba2",
      route: "/dashboard"
    },
    {
      id: "publicar",
      nombre: "Publicar",
      icono: <FolderPlus size={20} />,
      color: "#059669",
      route: "#",
      hasSubmenu: true,
      subItems: [
        { id: "presupuestos-obra", label: "Presupuestos para mi obra", description: "Crea y gestiona el presupuesto de tu obra privada.", icon: <FileTextIcon size={16} />, route: "/obrasprivadas/crear", directRoute: "/obrasprivadas/crear" },
        { id: "cotizar-materiales", label: "Pedir una cotización", description: "Solicita cotizaciones de materiales a proveedores.", icon: <DollarSign size={16} />, route: "/cotizaciondemateriales/crear", directRoute: "/cotizaciondemateriales/crear" },
        { id: "publicar-oferta-trabajo", label: "Vacante laboral", description: "Publicá una vacante para que profesionales se postulen a tu búsqueda.", icon: <FilePlus size={16} />, route: "/mercado-de-trabajo/crearoferta", directRoute: "/mercado-de-trabajo/crearoferta" },
        { id: "ofrecer-servicios", label: "Publicar posteos o productos/servicios", description: "Publica posteos o productos. Se publican en Explorar posteos, servicios y productos.", icon: <Package size={16} />, route: "#", directRoute: "#" }
      ]
    },
    {
      id: "explorar",
      nombre: "Explorar",
      icono: <Search size={20} />,
      color: "#234ba2",
      route: "#",
      hasSubmenu: true,
      subItems: [
        { id: "cotizar-obras", label: "Cotizar en obras privadas", description: "Encuentra obras privadas donde puedas cotizar.", icon: <Receipt size={16} />, route: "/obrasprivadas/explorar", directRoute: "/obrasprivadas/explorar" },
        { id: "obras-publicas", label: "Obras públicas", description: "Explora licitaciones y obras públicas.", icon: <Building size={16} />, route: "/obraspublicas", directRoute: "/obraspublicas" },
        { id: "cotizaciones-materiales", label: "Explorar cotizaciones", description: "Busca y responde a pedidos de cotización de materiales.", icon: <ClipboardList size={16} />, route: "/cotizaciondemateriales/explorar", directRoute: "/cotizaciondemateriales/explorar" },
        { id: "encontrar-profesionales", label: "Explorar perfiles", description: "Encuentra profesionales por especialidad o zona.", icon: <Users size={16} />, route: "/buscar-perfiles", directRoute: "/buscar-perfiles" },
        { id: "explorar-servicios", label: "Explorar posteos, servicios y productos", description: "Descubre posteos, servicios y productos de la comunidad.", icon: <Store size={16} />, route: "/marketplace/explorar", directRoute: "/marketplace/explorar" },
        { id: "postularme-ofertas", label: "Postularme a ofertas de trabajo", description: "Encuentra ofertas de trabajo y postúlate.", icon: <UserPlus size={16} />, route: "/bolsa-trabajo", directRoute: "/bolsa-trabajo" }
      ]
    },
    {
      id: "mis-contenidos",
      nombre: "Mis contenidos",
      icono: <FolderKanban size={20} />,
      color: "#1a3a7a",
      route: "#",
      hasSubmenu: true,
      subItems: [
        { id: "mis-proyectos", label: "Mis proyectos en Edify", description: "Accede a tus proyectos y documentación.", icon: <FolderOpen size={16} />, route: "#", directRoute: "#" },
        { id: "mis-cotizaciones-materiales", label: "Mis cotizaciones de materiales", description: "Revisa tus cotizaciones enviadas y recibidas.", icon: <FolderKanban size={16} />, route: "#", directRoute: "#" },
        { id: "mis-postulaciones", label: "Mis postulaciones", description: "Sigue el estado de tus postulaciones a ofertas.", icon: <LockKeyhole size={16} />, route: "#", directRoute: "#" },
        { id: "mis-productos-servicios", label: "Mis productos/servicios", description: "Gestiona tus publicaciones en el marketplace.", icon: <ShoppingBag size={16} />, route: "#", directRoute: "#" }
      ]
    },
    {
      id: "herramientas",
      nombre: "Herramientas",
      icono: <Calculator size={20} />,
      color: "#45a7ff",
      route: "#",
      hasSubmenu: true,
      subItems: [
        { id: "estimacion-costos", label: "Estimación de costos", description: "Calcula estimaciones de costos de materiales.", icon: <Calculator size={16} />, route: "/cotizaciondemateriales/estimacion-costos", directRoute: "/cotizaciondemateriales/estimacion-costos" }
      ]
    },
    {
      id: "notificaciones",
      nombre: "Notificaciones",
      icono: <Bell size={20} />,
      color: "#234ba2",
      route: "/notificaciones",
      badge: newNotifications
    },
    {
      id: "ayuda",
      nombre: "Ayuda",
      icono: <CircleHelp size={20} />,
      color: "#234ba2",
      route: "/faq"
    },
    {
      id: "admin",
      nombre: "Admin",
      icono: <Shield size={20} />,
      color: "#dc2626",
      route: "/backoffice",
      adminOnly: true,
      hasSubmenu: true,
      subItems: [
        { id: "backoffice", label: "Backoffice", description: "Panel de administración de Edify.", icon: <Settings size={16} />, route: "/backoffice", directRoute: "/backoffice" }
      ]
    }
  ];

  const filteredButtons = navButtons.filter(button => 
    !button.adminOnly || isUserAdmin
  );

  useEffect(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const currentTab = getProfileTabFromQuery(searchParams);
    if (pathname === "/dashboard") {
      setActiveRoute("dashboard");
      return;
    }
    if (pathname.startsWith("/profesionales")) {
      if (userData) {
        const segments = pathname.split("/").filter(Boolean);
        if (segments[1] === String(userData.id) && currentTab && PROFILE_TAB_TO_NAV_ITEM[currentTab]) {
          setActiveRoute("mis-contenidos");
          return;
        }
      }
      setActiveRoute("explorar");
      return;
    }
    if (pathname === "/obrasprivadas/crear") setActiveRoute("publicar");
    else if (pathname.startsWith("/obrasprivadas") || pathname.startsWith("/obraspublicas")) setActiveRoute("explorar");
    else if (pathname === "/cotizaciondemateriales/estimacion-costos") setActiveRoute("herramientas");
    else if (pathname === "/cotizaciondemateriales/crear") setActiveRoute("publicar");
    else if (pathname.startsWith("/cotizaciondemateriales")) setActiveRoute("explorar");
    else if (pathname.startsWith("/mercado-de-trabajo")) setActiveRoute("publicar");
    else if (pathname.startsWith("/bolsa-trabajo")) setActiveRoute("explorar");
    else if (pathname.startsWith("/marketplace")) setActiveRoute("explorar");
    else if (pathname.startsWith("/buscar-perfiles")) setActiveRoute("explorar");
    else if (pathname.startsWith("/notificaciones")) setActiveRoute("notificaciones");
    else if (pathname.startsWith("/faq")) setActiveRoute("ayuda");
    else if (pathname.startsWith("/backoffice")) setActiveRoute("admin");
    else setActiveRoute("");
  }, [location.pathname, location.search, userData]);

  const handleButtonClick = (button: NavButton) => {
    if (button.hasSubmenu && button.subItems) {
      setMenuScreen(button.id);
    } else {
      navigate(button.route);
      setIsMenuOpen(false);
    }
  };

  const handleSubItemClick = (subItem: NavSubItem) => {
    if (subItem.id === "ofrecer-servicios") {
      openFeedTextModal?.({ text: true });
      setIsMenuOpen(false);
      setMenuScreen("main");
      return;
    }
    const profileTab = PROFILE_SUBITEM_TAB_MAPPING[subItem.id];
    if (profileTab) {
      const { isProfile, userId: pathUserId, userName: pathUserName } = profilePathInfo;
      const ownUserId = userData?.id ? String(userData.id) : null;
      const ownDisplayName = getUserDisplayName(userData);
      const candidateProfileData = (() => {
        if (!isProfile) return userData;
        if (ownUserId && pathUserId === ownUserId) return userData;
        if (profileVisualizerData && profileVisualizerData.id && String(profileVisualizerData.id) === pathUserId) {
          return profileVisualizerData;
        }
        return profileVisualizerData;
      })();
      const candidateId = candidateProfileData?.id ? String(candidateProfileData.id) : (isProfile ? pathUserId : ownUserId);
      const candidateName = getUserDisplayName(candidateProfileData) || (isProfile ? pathUserName : null) || ownDisplayName || pathUserName || 'perfil';
      if (candidateId && candidateName) {
        const profileUrl = buildProfileUrl(candidateId, candidateName, profileTab);
        navigate(profileUrl);
        setIsMenuOpen(false);
        setMenuScreen("main");
        return;
      }
      setIsMenuOpen(false);
      setMenuScreen("main");
      return;
    }
    navigate(subItem.route);
    setIsMenuOpen(false);
    setMenuScreen("main");
  };

  const goBackToMain = () => setMenuScreen("main");
  const closeMenu = () => {
    setIsMenuOpen(false);
    setMenuScreen("main");
  };

  const handleLogoutClick = () => closeMenu();
  const handleProfileClick = () => {
    if (userData?.id) navigate(`/perfil/${userData.id}`);
    closeMenu();
  };
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const currentSection = menuScreen !== "main" ? filteredButtons.find(b => b.id === menuScreen) : null;


  return (
    <nav className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logo}>
          <img
            src={logoNavbar}
            alt="Edify"
            className={styles.logoImage}
          />
        </div>
        <button
          className={styles.menuButton}
          onClick={toggleMenu}
          aria-label="Abrir menú"
        >
          <Menu size={24} />
        </button>
      </div>

      <ModalOverlay
        isOpen={isMenuOpen}
        onClose={closeMenu}
        zIndex={1001}
      >
        <div className={styles.menuOverlayWrap}>
          <div className={styles.menuBackdrop} onClick={closeMenu} aria-hidden />
          <div className={styles.menuContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.menuPanelWrap}>
              <div
                className={styles.menuScreenMain}
                style={{ transform: menuScreen === "main" ? "translateX(0)" : "translateX(-100%)" }}
              >
                <div className={styles.mainScreenHeader}>
                  <span className={styles.mainScreenTitle}>Menú</span>
                  <button
                    type="button"
                    className={styles.mainScreenClose}
                    onClick={closeMenu}
                    aria-label="Cerrar menú"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className={styles.profileSection} onClick={handleProfileClick}>
                <div className={styles.profileImage}>
                  {!imageError && profileImage ? (
                    <img src={profileImage} alt="Perfil" className={styles.profileImg} />
                  ) : initials ? (
                    <div className={styles.profileInitials}>{initials}</div>
                  ) : (
                    <div className={styles.profileDefault}><User size={24} /></div>
                  )}
                </div>
                <div className={styles.profileInfo}>
                  <h3 className={styles.profileName}>
                    {userData?.type === "COMPANY" ? userData?.razon_social : `${userData?.name} ${userData?.last_name}`}
                  </h3>
                  <p className={styles.profileEmail}>{userData?.username}</p>
                </div>
              </div>
              <div className={styles.navigationSection}>
                <span className={styles.menuCategoryLabel}>NAVEGACIÓN</span>
                {filteredButtons.map((button) => (
                  <div key={button.id} className={styles.navItemContainer}>
                    <BotonModern
                      button={button}
                      isActive={activeRoute === button.id}
                      onClick={() => handleButtonClick(button)}
                      hasSubmenu={button.hasSubmenu}
                      isSubmenuExpanded={false}
                      chevronRight={Boolean(button.hasSubmenu)}
                      hideIcon
                    />
                  </div>
                ))}
              </div>
              <div className={styles.logoutSection} onClick={handleLogoutClick}>
                <LogOutMenu />
              </div>
            </div>

            <div
              className={styles.menuScreenSub}
              style={{ transform: menuScreen === "main" ? "translateX(100%)" : "translateX(0)" }}
            >
              {currentSection && (
                <>
                  <div className={styles.subScreenHeader}>
                    <button
                      type="button"
                      className={styles.subScreenBack}
                      onClick={goBackToMain}
                      aria-label="Volver"
                    >
                      <ArrowLeft size={22} />
                    </button>
                    <h2 className={styles.subScreenTitle}>{currentSection.nombre}</h2>
                    <button
                      type="button"
                      className={styles.subScreenClose}
                      onClick={closeMenu}
                      aria-label="Cerrar menú"
                    >
                      <X size={22} />
                    </button>
                  </div>
                  <div className={styles.subScreenContent}>
                    <span className={styles.menuCategoryLabel}>OPCIONES</span>
                    {currentSection.subItems?.map((subItem) => (
                      <button
                        key={subItem.id}
                        type="button"
                        className={styles.submenuItem}
                        onClick={() => handleSubItemClick(subItem)}
                      >
                        <div className={styles.submenuItemText}>
                          <span className={styles.submenuItemTitle}>{subItem.label}</span>
                          <span className={styles.submenuItemDescription}>{subItem.description}</span>
                        </div>
                        <ChevronRight size={20} className={styles.submenuChevron} />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
      </ModalOverlay>
    </nav>
  );
};

const mapStateToProps = (state: any) => ({
  userData: state.general.userData,
  profileVisualizerData: state.general.profileVisualizer,
  newNotifications: state.dashboard.newNotifications,
});

export default connect(mapStateToProps, { openFeedTextModal })(BotoneraModern);
